import React from 'react';
import { Platform } from 'react-native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import appNavigationNames from '../../../utils/appNavigationNames';
import { TabName, consumerAppMobileTabConfig, professionalAppTabConfig } from './tabConfig';
import TabBar from './TabBar';
import { useAppType } from '../../../hooks/useAppType';

const Tab = createBottomTabNavigator();

export type TabNavigationOwnProps = {};

export type TabNavigationProps = TabNavigationOwnProps;

/**
 * Navigation entry for a user that has logged in or signed up and has registered (entered required user data).
 */
const BottomTabNavigation: React.FC<TabNavigationProps> = () => {
    const insets = useSafeAreaInsets();
    const tabConfig = useAppType() === 'professional' ? professionalAppTabConfig : consumerAppMobileTabConfig;

    /**
     * When navigated directly to a nested screen on the web, the tab press will navigate to the root
     * of the tab because it internally calls "popToTop" of the tab's navigation and there is no other screen on the stack.
     *
     * If a tab is pressed and the nested navigation depth === 1 (only the nested screen is on the stack), manually
     * navigate to the root screen of the tab.
     *
     * Also, if the tab is already active, navigate to the root screen of the tab.
     *
     * If any callback is supplied, the behavior gets wonky and will navigate "back" and not to the top of the stack.  Return
     * undefined to use default behavior if this edge case isn't present.
     */
    const getTabPressListener = (
        navigation: any,
        tabName: TabName,
        tabRootScreen: string,
    ): ((e: any) => void | undefined) => {
        const tabState = navigation.getState();
        const currentIndex = tabState.index; // This gives you the current index of the active tab
        const activeTab = tabState.routes[currentIndex]; // This gives you the active tab route object
        const activeTabName = activeTab.name; // This is the name of the active tab
        const tabNavigationState = tabState?.routes?.find?.((route: any) => route.name === tabName)?.state;
        const tabHistoryDepth = tabNavigationState?.routes?.length;

        return tabHistoryDepth === 1 && activeTabName !== tabName
            ? (undefined as any)
            : (e) => {
                  e.preventDefault();
                  navigation.navigate(tabName, {
                      screen: tabRootScreen,
                  });
              };
    };

    return (
        <Tab.Navigator
            tabBar={(props) => <TabBar {...props} />}
            initialRouteName={appNavigationNames.tabs.profiles}
            backBehavior={Platform.select({ web: 'history', default: 'firstRoute' })}
            safeAreaInsets={{ bottom: insets.bottom + 8 }}
            screenOptions={{
                headerShown: false,
            }}
        >
            {tabConfig.map((option) => (
                <Tab.Screen
                    key={option.name}
                    name={option.name}
                    listeners={({ navigation }) => ({
                        tabPress: getTabPressListener(navigation, option.name, option.tabRootScreen),
                    })}
                    options={{}}
                    component={option.component}
                />
            ))}
        </Tab.Navigator>
    );
};

export default React.memo(BottomTabNavigation);
